import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDownloadURL, uploadBytesResumable, ref } from "firebase/storage";
import { storage } from "../firebase.config";
import { addDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase.config";
import { useAuth } from "../contexts/AuthContext";
import { cities } from "../data/citiesData";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// 🔍 SEO: use react-helmet-async (already wired via HelmetProvider in App.jsx)
import { Helmet } from "react-helmet-async";

/* =========================================================
   🔍 SEO CONSTANTS
   ========================================================= */
const SITE_URL = "https://www.estateghar.com"; // ⚠️ change to your real domain
const SITE_NAME = "EstateGhar";
const CANONICAL_PATH = "/register-agency";
const CANONICAL_URL = `${SITE_URL}${CANONICAL_PATH}`;

const PAGE_TITLE =
  "Register Your Real Estate Agency in Pakistan | EstateGhar Partner Program";
const PAGE_DESCRIPTION =
  "Register your real estate agency on EstateGhar for free. Get a verified badge, quality leads from serious buyers, priority support, and a dedicated agency profile page. Join 500+ trusted Pakistani agencies.";

const CreateAgencyProfile = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [thumbnail, setThumbnail] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [sellerName, setSellerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [location, setLocation] = useState("");
  const [ranking, setRanking] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [errors, setErrors] = useState({});

  // Pakistan cities list
  const pakistanCities = cities.map((city) => city.name);

  // Check if user already has an agency profile
  useEffect(() => {
    const checkExistingProfile = async () => {
      if (!currentUser) return;

      try {
        const q = query(
          collection(db, "Featured-Agents"),
          where("userId", "==", currentUser.uid)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const profileDoc = querySnapshot.docs[0];
          navigate(`/agent-profile/${profileDoc.id}`);
        }
      } catch (error) {
        console.error("Error checking profile:", error);
      } finally {
        setCheckingProfile(false);
      }
    };

    if (!authLoading) {
      if (!currentUser) {
        navigate("/sign-in", { state: { from: "/register-agency" } });
      } else {
        checkExistingProfile();
      }
    }
  }, [currentUser, authLoading, navigate]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnail(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image to Firebase Storage
  const uploadImage = async (file) => {
    return new Promise((resolve, reject) => {
      const uniqueName = `${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `/agency-thumbnails/${uniqueName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const prog = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setProgress(prog);
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!sellerName.trim()) {
      newErrors.sellerName = "Agency name is required";
    } else if (sellerName.length < 3) {
      newErrors.sellerName = "Agency name must be at least 3 characters";
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (phoneNumber.replace(/[^\d]/g, "").length < 10) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    if (!address.trim()) {
      newErrors.address = "Street address is required";
    }

    if (!city) {
      newErrors.city = "Please select a city";
    }

    if (!location.trim()) {
      newErrors.location = "Please enter a location/area";
    }

    if (!ranking) {
      newErrors.ranking = "Please select a ranking";
    } else if (ranking < 1 || ranking > 5) {
      newErrors.ranking = "Ranking must be between 1 and 5";
    }

    if (!thumbnailFile && !thumbnail) {
      newErrors.thumbnail = "Please upload an agency logo";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save agency profile to Firestore
  const saveAgencyProfile = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = document.querySelector(".border-red-400");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setSubmitting(true);
    setUploading(true);

    try {
      let imageUrl = thumbnail;
      if (thumbnailFile) {
        imageUrl = await uploadImage(thumbnailFile);
      }

      const data = {
        thumbnail: imageUrl,
        sellerName: sellerName.trim(),
        phoneNumber,
        address: address.trim(),
        city: city,
        location: location.trim(),
        ranking: Number(ranking),
        userId: currentUser.uid,
        userEmail: currentUser.email,
        createdAt: new Date().toISOString(),
        status: "active",
        totalListings: 0,
        totalSales: 0,
      };

      const docRef = await addDoc(collection(db, "Featured-Agents"), data);

      toast.success(
        "🎉 Agency profile created successfully! Redirecting to your profile...",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      setTimeout(() => {
        navigate(`/agent-profile/${docRef.id}`);
      }, 2000);
    } catch (e) {
      console.error("Error adding document: ", e);
      toast.error("Failed to create agency profile. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  // Handle input changes
  const handleSellerNameChange = (e) => {
    setSellerName(e.target.value);
    if (errors.sellerName) setErrors({ ...errors, sellerName: "" });
  };

  const handlePhoneNumberChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d]/g, "");
    if (rawValue.length > 10) return;
    let formatted = rawValue;
    if (rawValue.length >= 4 && rawValue.length <= 6) {
      formatted = `${rawValue.slice(0, 3)}-${rawValue.slice(3)}`;
    } else if (rawValue.length > 6) {
      formatted = `${rawValue.slice(0, 3)}-${rawValue.slice(3, 6)}-${rawValue.slice(6, 10)}`;
    }
    setPhoneNumber(formatted);
    if (errors.phoneNumber) setErrors({ ...errors, phoneNumber: "" });
  };

  const handleAddressChange = (e) => {
    setAddress(e.target.value);
    if (errors.address) setErrors({ ...errors, address: "" });
  };

  const handleCityChange = (e) => {
    setCity(e.target.value);
    if (errors.city) setErrors({ ...errors, city: "" });
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    if (errors.location) setErrors({ ...errors, location: "" });
  };

  const handleRankingChange = (e) => {
    setRanking(e.target.value);
    if (errors.ranking) setErrors({ ...errors, ranking: "" });
  };

  // 🔍 SEO: structured data (WebPage + Service + FAQ)
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": CANONICAL_URL,
        url: CANONICAL_URL,
        name: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
        inLanguage: "en-PK",
        isPartOf: {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          url: SITE_URL,
          name: SITE_NAME,
        },
        potentialAction: {
          "@type": "RegisterAction",
          target: CANONICAL_URL,
          name: "Register your real estate agency on EstateGhar",
        },
      },
      {
        "@type": "Service",
        name: "Real Estate Agency Listing & Lead Generation",
        provider: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
        },
        areaServed: {
          "@type": "Country",
          name: "Pakistan",
        },
        serviceType:
          "Real estate agency registration, verified listing, and lead generation",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "PKR",
          description:
            "Free agency registration with verified badge, quality leads, and priority support.",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: SITE_URL,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Register Agency",
            item: CANONICAL_URL,
          },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Is registering my real estate agency on EstateGhar free?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, registering your agency on EstateGhar is completely free. You get a verified badge, a dedicated agency profile page, and access to quality leads from serious buyers.",
            },
          },
          {
            "@type": "Question",
            name: "What do I need to register my agency?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "You need your agency name, logo, phone number, city, area, and complete street address. Registration takes less than two minutes.",
            },
          },
          {
            "@type": "Question",
            name: "What benefits do registered agencies get on EstateGhar?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Registered agencies get a verified badge, direct buyer leads, a dedicated agency profile page, priority 24/7 support, and increased visibility across EstateGhar's Pakistani real estate network.",
            },
          },
        ],
      },
    ],
  };

  // Show loading state
  if (authLoading || checkingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* =========================================================
          🔍 SEO: React Helmet
          ========================================================= */}
      <Helmet>
        <html lang="en-PK" />
        <title>{PAGE_TITLE}</title>
        <meta name="title" content={PAGE_TITLE} />
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="register real estate agency, agency registration Pakistan, real estate agency portal, partner with EstateGhar, real estate agent signup Pakistan, list agency Pakistan, property dealer registration, real estate agent Karachi, real estate agent Lahore, real estate agent Islamabad"
        />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1"
        />
        <meta name="author" content={SITE_NAME} />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#059669" />
        <link rel="canonical" href={CANONICAL_URL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content="en_PK" />
        <meta
          property="og:image"
          content={`${SITE_URL}/og-image.jpg`}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Register your real estate agency on EstateGhar"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta
          name="twitter:image"
          content={`${SITE_URL}/twitter-image.jpg`}
        />

        {/* hreflang */}
        <link rel="alternate" hrefLang="en-PK" href={CANONICAL_URL} />
        <link rel="alternate" hrefLang="x-default" href={CANONICAL_URL} />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <ToastContainer />

      {/* Top Banner */}
      <div className="bg-gradient-to-r from-emerald-900 via-green-800 to-emerald-900 py-4 shadow-lg border-b border-amber-400/30">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="hidden sm:flex items-center space-x-2">
                <svg
                  className="w-6 h-6 text-amber-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span className="text-amber-400 font-semibold">
                  Partner With Us:
                </span>
              </div>
              <span className="text-white text-base md:text-lg font-medium">
                Create Your Agency Profile
              </span>
            </div>
            <div className="text-amber-400/80 text-sm hidden md:block">
              Join 500+ trusted agencies
            </div>
          </div>
        </div>
      </div>

      {/* 🔍 SEO: main landmark for accessibility + crawlers */}
      <main
        className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8"
        itemScope
        itemType="https://schema.org/WebPage"
      >
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <header className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            {/* 🔍 SEO: single H1 */}
            <h1
              className="text-4xl font-bold text-gray-900 mb-2"
              itemProp="name"
            >
              Register Your Agency
            </h1>
            {/* 🔍 SEO: descriptive intro paragraph containing target keywords */}
            <p
              className="text-lg text-gray-600 max-w-2xl mx-auto"
              itemProp="description"
            >
              Join Pakistan's fastest-growing real estate network. Get a verified
              agency badge, quality buyer leads, and a dedicated agency profile
              page — completely free.
            </p>
          </header>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <div>
                  <h2 className="text-white font-semibold text-lg">
                    Agency Information
                  </h2>
                  <p className="text-emerald-100 text-sm">
                    Fill in your agency details below
                  </p>
                </div>
              </div>
            </div>

            {/* 🔍 SEO: proper form semantics with autoComplete + aria */}
            <form
              onSubmit={saveAgencyProfile}
              className="p-6 md:p-8 space-y-6"
              noValidate
            >
              {/* Agency Logo Upload */}
              <div className="space-y-3">
                <label
                  htmlFor="agency-logo"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  Agency Logo <span className="text-red-500">*</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-xl p-6 transition-all ${
                    errors.thumbnail
                      ? "border-red-400 bg-red-50"
                      : "border-gray-300 hover:border-emerald-400"
                  }`}
                >
                  <div className="flex flex-col items-center space-y-4">
                    {thumbnail ? (
                      <div className="relative group">
                        <img
                          src={thumbnail}
                          alt="Agency logo preview"
                          className="w-32 h-32 object-cover rounded-full border-4 border-emerald-500 shadow-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setThumbnail("");
                            setThumbnailFile(null);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                          aria-label="Remove agency logo"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                          <svg
                            className="w-12 h-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div className="text-center">
                          <label
                            htmlFor="agency-logo"
                            className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition inline-flex items-center space-x-2"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                              />
                            </svg>
                            <span>Choose Logo</span>
                          </label>
                          <input
                            id="agency-logo"
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            PNG, JPG up to 5MB
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {uploading && progress > 0 && progress < 100 && (
                    <div className="mt-4">
                      <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-1 text-center">
                        Uploading: {progress}%
                      </p>
                    </div>
                  )}
                </div>
                {errors.thumbnail && (
                  <p className="text-red-500 text-sm mt-1" role="alert">
                    {errors.thumbnail}
                  </p>
                )}
              </div>

              {/* Agency Name */}
              <div>
                <label
                  htmlFor="agency-name"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  Agency Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <input
                    id="agency-name"
                    name="agencyName"
                    type="text"
                    autoComplete="organization"
                    placeholder="e.g., EstateGhar Real Estate"
                    value={sellerName}
                    onChange={handleSellerNameChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.sellerName
                        ? "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-emerald-400 focus:border-emerald-400"
                    }`}
                    aria-invalid={Boolean(errors.sellerName)}
                    aria-describedby={
                      errors.sellerName ? "agency-name-error" : undefined
                    }
                  />
                </div>
                {errors.sellerName && (
                  <p
                    id="agency-name-error"
                    className="text-red-500 text-sm mt-1"
                    role="alert"
                  >
                    {errors.sellerName}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label
                  htmlFor="agency-phone"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-xl" aria-hidden="true">
                      🇵🇰
                    </span>
                  </div>
                  <input
                    id="agency-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="numeric"
                    placeholder="3XX-XXX-XXXX"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    maxLength={15}
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.phoneNumber
                        ? "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-emerald-400 focus:border-emerald-400"
                    }`}
                    aria-invalid={Boolean(errors.phoneNumber)}
                    aria-describedby={
                      errors.phoneNumber ? "agency-phone-error" : undefined
                    }
                  />
                </div>
                {errors.phoneNumber && (
                  <p
                    id="agency-phone-error"
                    className="text-red-500 text-sm mt-1"
                    role="alert"
                  >
                    {errors.phoneNumber}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Enter 10-digit mobile number
                </p>
              </div>

              {/* City Selection */}
              <div>
                <label
                  htmlFor="agency-city"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  City <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                  </div>
                  <select
                    id="agency-city"
                    name="city"
                    autoComplete="address-level2"
                    value={city}
                    onChange={handleCityChange}
                    className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 transition appearance-none bg-white ${
                      errors.city
                        ? "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-emerald-400 focus:border-emerald-400"
                    }`}
                    aria-invalid={Boolean(errors.city)}
                  >
                    <option value="">Select a city</option>
                    {pakistanCities.map((cityName) => (
                      <option key={cityName} value={cityName}>
                        {cityName}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {errors.city && (
                  <p className="text-red-500 text-sm mt-1" role="alert">
                    {errors.city}
                  </p>
                )}
              </div>

              {/* Location/Area */}
              <div>
                <label
                  htmlFor="agency-location"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  Location / Area <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <input
                    id="agency-location"
                    name="area"
                    type="text"
                    autoComplete="address-level3"
                    placeholder="e.g., DHA Phase 2, Gulberg, Bahria Town, F-8 Markaz"
                    value={location}
                    onChange={handleLocationChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.location
                        ? "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-emerald-400 focus:border-emerald-400"
                    }`}
                    aria-invalid={Boolean(errors.location)}
                  />
                </div>
                {errors.location && (
                  <p className="text-red-500 text-sm mt-1" role="alert">
                    {errors.location}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Enter your specific location, sector, or area name
                </p>
              </div>

              {/* Street Address */}
              <div>
                <label
                  htmlFor="agency-address"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  Street Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                  </div>
                  <textarea
                    id="agency-address"
                    name="street-address"
                    autoComplete="street-address"
                    placeholder="Complete street address, building name, office number, landmark"
                    value={address}
                    onChange={handleAddressChange}
                    rows="3"
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.address
                        ? "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-emerald-400 focus:border-emerald-400"
                    }`}
                    aria-invalid={Boolean(errors.address)}
                  />
                </div>
                {errors.address && (
                  <p className="text-red-500 text-sm mt-1" role="alert">
                    {errors.address}
                  </p>
                )}
              </div>

              {/* Ranking */}
              <div>
                <label
                  htmlFor="agency-ranking"
                  className="block text-gray-700 font-semibold mb-2"
                >
                  Agency Rating <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-4" role="radiogroup" aria-label="Agency rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRanking(star)}
                        className={`text-3xl transition-transform hover:scale-110 ${
                          ranking >= star ? "text-yellow-400" : "text-gray-300"
                        }`}
                        aria-label={`${star} star${star > 1 ? "s" : ""}`}
                        aria-pressed={Number(ranking) === star}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <select
                    id="agency-ranking"
                    name="ranking"
                    value={ranking}
                    onChange={handleRankingChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.ranking
                        ? "border-red-400 focus:ring-red-400"
                        : "border-gray-300 focus:ring-emerald-400 focus:border-emerald-400"
                    }`}
                  >
                    <option value="">Select experience level</option>
                    <option value="1">⭐ Beginner (1 Star)</option>
                    <option value="2">⭐⭐ Intermediate (2 Stars)</option>
                    <option value="3">⭐⭐⭐ Professional (3 Stars)</option>
                    <option value="4">⭐⭐⭐⭐ Advanced (4 Stars)</option>
                    <option value="5">⭐⭐⭐⭐⭐ Expert (5 Stars)</option>
                  </select>
                </div>
                {errors.ranking && (
                  <p className="text-red-500 text-sm mt-1" role="alert">
                    {errors.ranking}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white py-4 rounded-xl text-lg font-semibold hover:from-emerald-700 hover:to-green-700 focus:ring-4 focus:ring-emerald-300 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center space-x-2">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Creating Agency Profile...</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center space-x-2">
                      <span>Register Agency</span>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Benefits Section */}
          <section
            className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
            aria-labelledby="benefits-heading"
          >
            {/* 🔍 SEO: H2 for benefits */}
            <h2 id="benefits-heading" className="sr-only">
              Benefits of registering your agency on EstateGhar
            </h2>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Verified Badge</h3>
              <p className="text-sm text-gray-600">
                Get verified status and build trust with buyers
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Lead Generation</h3>
              <p className="text-sm text-gray-600">
                Get quality leads from serious buyers
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">
                Priority Support
              </h3>
              <p className="text-sm text-gray-600">
                24/7 dedicated support for agency partners
              </p>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default CreateAgencyProfile;